import React, { useState, useEffect } from 'react';
import axios from 'axios';
import WithdrawForm from './WithdrawForm';
import './Withdraw.css'; // Import the CSS file for styling

const Withdraw = ({ userDetails }) => {
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [earnings, setEarnings] = useState(0);
  const [fetchingEarnings, setFetchingEarnings] = useState(true);
  const [totalToWithdraw, setTotalToWithdraw] = useState(0);
  const [breakPayout, setBreakPayout] = useState(0);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showBreakInvestmentModal, setShowBreakInvestmentModal] = useState(false);
  const [withdrawalMessage, setWithdrawalMessage] = useState('');

  const { wallet, email, end_time, expectedWithdrawal, investedAmount } = userDetails || {};
  const fee = 2.6;
  const todaysDate = new Date();
  const matureDate = new Date(end_time);
  let diffTime = matureDate - todaysDate;
  let diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  // Fallback for invalid date calculations
  if (isNaN(diffDays)) diffDays = 'required processing';
  const isWithdrawalEnabled = diffDays <= 0;

  // Fetch earnings on mount or when email changes
  useEffect(() => {
    if (!email) {
      setError('Email is required to fetch earnings.');
      setFetchingEarnings(false);
      return;
    }

    const fetchEarnings = async () => {
      setError('');
      try {
        const response = await axios.post('https://sharedvision-partners.online/v1/API/get_earnings.php', { email });
        if (response.status === 200 && response.data.success) {
          setEarnings(response.data.earnings || 0);
        } else {
          setError(response.data.message || 'Failed to fetch earnings.');
        }
      } catch (error) {
        setError('An error occurred. Please try again later.');
      } finally {
        setFetchingEarnings(false);
      }
    };

    fetchEarnings();
  }, [email]);

  // Calculate payouts and enable/disable submit
  useEffect(() => {
    const { payout, BreakPayout } = calculatePayout(earnings, expectedWithdrawal, investedAmount);
    setTotalToWithdraw(payout);
    setBreakPayout(BreakPayout);

    setIsSubmitDisabled(!(earnings >= 5 && expectedWithdrawal > 0));
   
  }, [earnings, expectedWithdrawal, investedAmount]);

  // Update withdrawal message based on withdrawal eligibility
  useEffect(() => {
    setWithdrawalMessage(
      isWithdrawalEnabled
        ? 'Your withdrawal is enabled now.'
        : `We do not consider Sundays as business days, your withdrawal will be enabled in ${diffDays} day${diffDays !== 1 ? 's' : ''} from today.`
    );
  }, [isWithdrawalEnabled, diffDays]);

  // Calculate payout amounts
  const calculatePayout = (earnings, expectedWithdrawal, investedAmount) => {
    const total = (parseFloat(expectedWithdrawal) || 0) + (parseFloat(earnings) || 0);
    let payout = total - fee;
    let BreakPayout = (0.40 * investedAmount) - fee;

    // Ensuring that BreakPayout is always a number and fixed to 3 decimal places
    BreakPayout = parseFloat(BreakPayout.toFixed(3));
    if (payout < 0) payout = 0;

    return { payout, BreakPayout };
  };

  // Normal form submit handler
  const handleNormalSubmit = async (e, email, wallet, totalToWithdraw) => {
      setLoading(true);
    try {
      console.log("Submitting normal form:", { email, wallet, totalToWithdraw });
      const response = await axios.post('https://sharedvision-partners.online/v1/API/withdraw.php', {
        email,
        total: totalToWithdraw,
        wallet
      });
      if (response.status === 200 && response.data.success) {
        setShowSuccessModal(true);
      } else {
        setError(response.data.message || 'Failed to process the withdrawal.');
      }
    } catch (error) {
      setError('There was an error processing your request. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Modal form submit handler
  const handleSubmitModal = async (email, wallet, BreakPayout) => {
    setLoading(true);
    try {
      console.log("Submitting modal form:", { email, wallet, BreakPayout });
      const response = await axios.post('https://sharedvision-partners.online/v1/API/withdraw.php', {
        email,
        total: BreakPayout,
        wallet
      });
      if (response.status === 200 && response.data.success) {
        setShowSuccessModal(true);
      } else {
        setError('Failed to process the withdrawal.');
      }
    } catch (error) {
      setError('There was an error processing your request. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const toggleFormVisibility = () => setShowForm(prev => !prev);

   // Handle close success modal and refresh the page
   const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    window.location.reload(); // Refresh the page after successful submission
  };

  return (
    <div>
      <h1>Withdrawal</h1>
      <p>{withdrawalMessage}</p>
      <p>
        We imposed a $1 handling fee, $1 crypto fee and $0.6 charity fee, in total = $2.6. This is mandatory for us. Thank you for understanding.
      </p>
      <p>You get $1 and 100,000 SVP token for each referral once they choose a plan, which will be added to your expected withdrawable balance.</p>

      <button onClick={toggleFormVisibility} disabled={!isWithdrawalEnabled}>
        {showForm ? 'Close Withdrawal Form' : 'Open Withdrawal Form'}
      </button>

      {showForm && (
        <WithdrawForm
          email={email}
          expectedWithdrawal={expectedWithdrawal}
          earnings={earnings}
          wallet={wallet}
          totalToWithdraw={totalToWithdraw}
          investedAmount={investedAmount}
          BreakPayout={breakPayout}
          error={error}
          fetchingEarnings={fetchingEarnings}
          isSubmitDisabled={isSubmitDisabled}
          loading={loading}
          onSubmitNormal={(e) => handleNormalSubmit(e, email, wallet, totalToWithdraw)} // Make sure to pass event here for normal submit
          onSubmitModal={handleSubmitModal}    // Use handleSubmitModal here
          showBreakInvestmentButton={true}
          handleOpenBreakInvestmentModal={() => setShowBreakInvestmentModal(true)}
        />
      )}

      {showSuccessModal && (
        <div className="success-modal">
          <div className="modal-content">
            <h2>Congrats! Your withdrawal is successful!</h2>
         <p>Your funds are on their way. Your funds should hit your account within 48hrs.</p>
            <button onClick={handleCloseSuccessModal}>OK</button> {/* Close modal and refresh the page */}
           </div>
        </div>
      )}

      {showBreakInvestmentModal && (
        <div>
          <h2>Break Investment Details</h2>
          <p>Here's the breakdown of your investment and its expected payout. Proceed with caution!</p>
          <button onClick={() => setShowBreakInvestmentModal(false)}>Close</button>
        </div>
      )}
    </div>
  );
};

export default Withdraw;
